import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { BrandDetail, resolveBrandId, TitleSubTitle } from "@platformx/utilities";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ShareWithBrandsDialogProps {
  isDialogOpen: boolean;
  /** True while the document's current brands are being fetched — the selectors are withheld so the
   *  dialog never shows stale values and then jumps. */
  isLoading?: boolean;
  brands: BrandDetail[];
  /** Document's own primary/default brand (the backend's brand_name field — a single value). */
  brandName?: string;
  /** Document's own secondary brands (the backend's secondary_brandname array field). */
  secondaryBrandNames?: string[];
  dialogTitle?: string;
  closeButtonHandle: () => void;
  confirmButtonHandle: (
    // eslint-disable-next-line no-unused-vars
    primaryBrandId: string,
    // eslint-disable-next-line no-unused-vars
    secondaryBrandIds: string[],
  ) => void;
}

export const ShareWithBrandsDialog = ({
  isDialogOpen,
  isLoading = false,
  brands,
  brandName = "",
  secondaryBrandNames = [],
  dialogTitle,
  closeButtonHandle,
  confirmButtonHandle,
}: ShareWithBrandsDialogProps) => {
  const { t } = useTranslation();
  // Documents saved before the two-field model hold a comma-separated list in brand_name
  // ("1,3"): first entry is the primary, the rest count as secondary. Values may also be
  // 1-based positions (legacy); resolve to slug brandIds for the UI.
  const [legacyPrimary = "", ...legacyExtras] = brandName
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const initialPrimaryBrandId = useMemo(
    () => resolveBrandId(legacyPrimary) || brands[0]?.brandId || "",
    [legacyPrimary, brands],
  );
  const legacyExtrasKey = legacyExtras.join(",");
  // Preselected as the document returned them (deduped). The primary never appears in the
  // secondary list, so it is not preselected there either.
  const initialSecondaryBrandIds = useMemo(
    () =>
      [...secondaryBrandNames, ...(legacyExtrasKey ? legacyExtrasKey.split(",") : [])]
        .map(resolveBrandId)
        .filter(
          (id, index, all) => id && all.indexOf(id) === index && id !== initialPrimaryBrandId,
        ),
    [secondaryBrandNames, legacyExtrasKey, initialPrimaryBrandId],
  );

  const [primaryBrandId, setPrimaryBrandId] = useState<string>(initialPrimaryBrandId);
  const [secondaryBrandIds, setSecondaryBrandIds] = useState<string[]>(initialSecondaryBrandIds);

  // Re-seed on open so a cancelled selection is not shown again.
  useEffect(() => {
    if (isDialogOpen) {
      setPrimaryBrandId(initialPrimaryBrandId);
      setSecondaryBrandIds(initialSecondaryBrandIds);
    }
  }, [isDialogOpen, initialPrimaryBrandId, initialSecondaryBrandIds]);

  // The chosen primary is not offered as a secondary: it leaves the secondary selection, while
  // every other brand (including the previous primary) stays available, unselected.
  const secondaryBrandOptions = useMemo(
    () => brands.filter((brand) => brand.brandId !== primaryBrandId),
    [brands, primaryBrandId],
  );

  const handlePrimaryChange = (event: SelectChangeEvent<string>) => {
    const newPrimaryId = event.target.value;
    setSecondaryBrandIds((prev) => prev.filter((id) => id !== newPrimaryId));
    setPrimaryBrandId(newPrimaryId);
  };

  const handleSecondaryChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setSecondaryBrandIds(typeof value === "string" ? value.split(",") : value);
  };

  const withoutPrimary = (ids: string[], primary: string) => ids.filter((id) => id !== primary);
  const effectiveSecondaryBrandIds = withoutPrimary(secondaryBrandIds, primaryBrandId);

  const before = withoutPrimary(initialSecondaryBrandIds, initialPrimaryBrandId).sort().join(",");
  const after = [...effectiveSecondaryBrandIds].sort().join(",");
  const hasChanges = primaryBrandId !== initialPrimaryBrandId || before !== after;

  const getBrandName = (brandId: string) =>
    brands.find((brand) => brand.brandId === brandId)?.brandName || "";

  return (
    <Dialog
      fullWidth
      open={isDialogOpen}
      onClose={closeButtonHandle}
      aria-labelledby='share-with-brands-title'
      data-testid='share-with-brands-dialog'
      sx={{
        ".Platform-x-Dialog-paper": {
          margin: { xs: "0", md: "20px", xl: "30px" },
          alignSelf: { xs: "flex-end", md: "center" },
          padding: { xs: "20px 15px", md: "25px" },
          width: { xs: "100%" },
          borderBottomLeftRadius: { xs: 0, md: "4px" },
          borderBottomRightRadius: { xs: 0, md: "4px" },
        },
      }}>
      <Box>
        <DialogTitle id='share-with-brands-title' component='h2' variant='h2medium' sx={{ p: 0 }}>
          {t("share_with_brands")}
          {dialogTitle ? ` - ${dialogTitle}` : ""}
        </DialogTitle>
        <Box sx={{ mt: "8px", mb: "16px" }}>
          <TitleSubTitle subTitle={t("share_with_brands_subtitle")} subTitleVariant='h7regular' />
        </Box>

        {isLoading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", py: "32px" }}
            role='status'
            aria-busy='true'
            aria-label={t("loading")}
            data-testid='share-with-brands-loading'>
            <CircularProgress size={28} />
          </Box>
        ) : brands?.length === 0 ? (
          <Typography variant='p4regular'>{t("no_more_data")}</Typography>
        ) : (
          <>
            <Box sx={{ mb: "16px" }}>
              <Typography variant='p3medium' sx={{ mb: "6px", display: "block" }}>
                {t("primary_brand")}
              </Typography>
              <Select
                fullWidth
                value={primaryBrandId}
                onChange={handlePrimaryChange}
                renderValue={(value) => getBrandName(value as string)}
                data-testid='primary-brand-select'
                MenuProps={{ PaperProps: { sx: { maxHeight: "300px" } } }}>
                {brands.map((brand) => (
                  <MenuItem key={brand.brandId} value={brand.brandId}>
                    <Radio
                      checked={primaryBrandId === brand.brandId}
                      onChange={() => undefined}
                      data-testid={`primary-brand-radio-${brand.brandId}`}
                    />
                    <Typography variant='p3regular'>{brand.brandName}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography variant='p3medium' sx={{ mb: "6px", display: "block" }}>
                {t("secondary_brand")}
              </Typography>
              <Select
                fullWidth
                multiple
                displayEmpty
                value={secondaryBrandIds}
                onChange={handleSecondaryChange}
                renderValue={(value) =>
                  (value as string[]).length > 0
                    ? (value as string[]).map(getBrandName).join(", ")
                    : t("select")
                }
                data-testid='secondary-brand-select'
                MenuProps={{ PaperProps: { sx: { maxHeight: "300px" } } }}>
                {secondaryBrandOptions.map((brand) => (
                  <MenuItem key={brand.brandId} value={brand.brandId}>
                    <Checkbox
                      checked={secondaryBrandIds.includes(brand.brandId)}
                      onChange={() => undefined}
                      inputProps={
                        { "data-testid": `secondary-brand-checkbox-${brand.brandId}` } as any
                      }
                    />
                    <Typography variant='p3regular'>{brand.brandName}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </>
        )}

        <DialogActions sx={{ px: 0, pb: 0, mt: "16px" }}>
          <Button
            variant='secondaryButton'
            sx={{ marginRight: "8px" }}
            onClick={closeButtonHandle}
            data-testid='share-with-brands-cancel'>
            {t("cancel")}
          </Button>
          <Button
            variant='primaryButton'
            disabled={isLoading || !hasChanges}
            onClick={() => confirmButtonHandle(primaryBrandId, effectiveSecondaryBrandIds)}
            data-testid='share-with-brands-confirm'>
            {t("confirm")}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ShareWithBrandsDialog;
